import React, { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Button from 'fe-design-base/molecules/Button/Button';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { isEmpty } from 'lodash';

import { getShiftMetadata } from 'features/timesheets/TimesheetsPage/TimecardModal/utils.js';
import {
  EVENT_ACTIONS,
  trackTimecardClick,
} from 'features/timesheets/tracking';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';

const { cx, cxEl } = cxHelpers('TimecardEditFooter');

const embeddedStyles = {
  fixed: true,
  bottom: 0,
  right: 0,
  w: '100%',
  pv: 20,
  pr: 56,
  spacebetween: false,
  justifyContentEnd: true,
  shadow: 'z1',
  bgcolor: 'mono0',
};

const Footer = ({
  onCancel,
  onSave,
  isSubmitting,
  isMobile,
  editableShift,
  closeModal,
  changesMade,
  setChangesMade,
  shift,
}) => {
  const hasFormErrors = !isEmpty(editableShift.get('form_errors')?.toJS());
  const isEmbedded = useIsEmbedded();

  const isInvalidEntry =
    isEmpty(editableShift.get('actual_start_at')) ||
    !editableShift.get('job_id') ||
    hasFormErrors;

  const handleClickSave = useCallback(() => {
    setChangesMade(false);
    window.sessionStorage.setItem('timeCardAdded', true);
    trackTimecardClick({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      properties: {
        ...getShiftMetadata(editableShift),
        clock_out_changed:
          shift.get('actual_end_at') !== editableShift.get('actual_end_at'),
      },
    });
    onSave();
  }, [onSave, setChangesMade, editableShift, shift]);

  const handleClickCancel = useCallback(() => {
    setChangesMade(false);
    const newEmptyTimecard = !editableShift.get('id');
    trackTimecardClick({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      properties: getShiftMetadata(editableShift),
    });
    return newEmptyTimecard ? closeModal() : onCancel();
  }, [setChangesMade, editableShift, closeModal, onCancel]);

  return (
    <Box
      className={cx()}
      shadow="z1"
      shadowDirection="top"
      row
      spacebetween
      ph={isMobile ? 8 : 24}
      pv={16}
      gap={16}
      {...(isEmbedded && embeddedStyles)}
    >
      <Button
        className={cxEl('cancelButton')}
        onClick={handleClickCancel}
        theme="default"
        disabled={isSubmitting}
        variant="tertiary"
      >
        {toI18n('timecard_modal_v3.footer.cancel')}
      </Button>
      <Button
        className={cxEl('submitButton')}
        onClick={handleClickSave}
        disabled={isInvalidEntry || !changesMade}
        isLoading={isSubmitting}
        variant="primary"
        mr={isEmbedded && 16}
        dataTestId="save-time-card-button"
      >
        {toI18n('timecard_modal_v3.footer.submit')}
      </Button>
    </Box>
  );
};

export default Footer;
